import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'
import { routerConfig } from './config'

// Obtenir le chemin de base depuis la configuration WordPress
const BASE_URL = window.zebuMemberArea?.basePath || '/'
const CURRENT_PATH = window.zebuMemberArea?.currentPath || '/'
const RESET_TOKEN = window.zebuMemberArea?.resetToken || ''

// Fonction pour nettoyer les chemins
const cleanPath = (path) => path.replace(/^\/+|\/+$/g, '')

// Layouts
import MainLayout from '@/layouts/MainLayout.vue'

// Auth Views
import LoginView from '@/views/Auth/LoginView.vue'
import RegisterView from '@/views/Auth/RegisterView.vue'
import ForgotPasswordView from '@/views/Auth/ForgotPasswordView.vue'
import ResetPasswordView from '@/views/Auth/ResetPasswordView.vue'
import VerifyEmailView from '@/views/Auth/VerifyEmailView.vue'

// Main Views
import DashboardView from '@/views/DashboardView.vue'
import MyHerdView from '@/views/MyHerdView.vue'
import SubscribeView from '@/views/SubscribeView.vue'
import PublicSubscribeView from '@/views/PublicSubscribeView.vue'
import CartView from '@/views/CartView.vue'
import AccountView from '@/views/AccountView.vue'
import ProjectDetailsView from '@/views/ProjectDetailsView.vue'
import GiftView from '@/views/GiftView.vue'
import DonateView from '@/views/DonateView.vue'
import PublicMembershipSuccessView from '@/views/payment/PublicMembershipSuccessView.vue'
import PublicMembershipFailedView from '@/views/payment/PublicMembershipFailedView.vue'
import PublicRegisterView from '@/views/PublicRegisterView.vue'
import DonateRedirectHandler from '@/views/DonateRedirectHandler.vue'

const routes = [
  {
    path: '/',
    component: MainLayout,
    children: [
      {
        path: '',
        redirect: 'dashboard'
      },
      // Routes publiques
      {
        path: 'verify-email/:token',
        name: 'verify-email',
        component: VerifyEmailView,
        meta: { requiresAuth: false }
      },
      {
        path: 'login',
        name: 'login',
        component: LoginView,
        beforeEnter: (to, from, next) => {
          const authStore = useAuthStore()
          if (authStore.isAuthenticated) {
            next('/dashboard')
          } else {
            next()
          }
        }
      },
      {
        path: 'register',
        name: 'register',
        component: RegisterView
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: ForgotPasswordView
      },
      {
        path: 'reset-password/:token',
        name: 'reset-password',
        component: ResetPasswordView
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: DashboardView,
        meta: { requiresAuth: false }
      },
      {
        path: 'subscribe',
        name: 'subscribe',
        component: SubscribeView,
        meta: { requiresAuth: false }
      },
      {
        path: 'public-subscribe',
        name: 'public-subscribe',
        component: PublicSubscribeView,
        meta: { requiresAuth: false }
      },
      {
        path: 'membership/success',
        name: 'membership-success',
        component: PublicMembershipSuccessView,
        meta: { requiresAuth: false }
      },
      {
        path: 'membership/failed',
        name: 'membership-failed',
        component: PublicMembershipFailedView,
        meta: { requiresAuth: false }
      },
      {
        path: 'gift',
        name: 'gift',
        component: GiftView,
        meta: { requiresAuth: false }
      },
      {
        path: 'donate',
        name: 'donate',
        component: DonateView,
        meta: { requiresAuth: false }
      },
      {
        path: 'plan/:id',
        name: 'PlanDetails',
        component: () => import('@/views/PlanDetailsView.vue'),
        meta: { requiresAuth: false }
      },
      // Routes protégées
      {
        path: 'my-herd',
        name: 'my-herd',
        component: MyHerdView,
        meta: { requiresAuth: true }
      },
      {
        path: 'projects/:id',
        name: 'projects',
        component: ProjectDetailsView,
        meta: { requiresAuth: false }
      },
      {
        path: 'cart',
        name: 'cart',
        component: CartView,
        meta: { requiresAuth: true }
      },
      {
        path: 'account',
        name: 'account',
        component: AccountView,
        meta: { requiresAuth: true }
      },
      {
        path: 'herd/:id',
        name: 'project-details',
        component: () => import('@/views/ProjectDetailsView.vue'),
        meta: { requiresAuth: true }
      },
      {
        path: 'payment/success',
        name: 'PaymentSuccess',
        component: () => import('@/views/PaymentSuccessView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Paiement confirmé'
        }
      },
      {
        path: 'payment/failed',
        name: 'PaymentFailed',
        component: () => import('@/views/PaymentFailedView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Échec du paiement'
        }
      },
      {
        path: 'donate/success',
        name: 'DonateSuccess',
        component: () => import('@/views/DonateSuccessView.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'donate/redirect',
        name: 'DonateRedirect',
        component: DonateRedirectHandler,
        meta: { requiresAuth: false }
      },
      {
        path: 'cart/success',
        name: 'CartSuccess',
        component: () => import('@/views/CartSuccessView.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'donate/failed',
        name: 'DonateFailed',
        component: () => import('@/views/DonateFailedView.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: '/register',
        name: 'register',
        component: PublicRegisterView
      },
      {
        path: '/register/confirmation/:email?',
        name: 'register-confirmation',
        component: () => import('@/views/RegisterConfirmationView.vue'),
        meta: {
          title: 'Confirmation d\'inscription',
          requiresAuth: false
        }
      },
      {
        path: '/register-with-plan',
        name: 'register-with-plan',
        component: () => import('@/views/RegisterWithPlanView.vue'),
        meta: {
          requiresGuest: true,
          title: 'Inscription avec plan - Zebu'
        }
      },
      {
        path: '/mon-cheptel/certificat/:id',
        name: 'CertificateView',
        component: () => import('@/components/Certificate/CertificateView.vue'),
        meta: {
          requiresAuth: true
        }
      }
    ]
  },
  // Route catch-all pour rediriger vers dashboard
  {
    path: '/:pathMatch(.*)*',
    redirect: '/dashboard'
  }
]

const router = createRouter({
  history: createWebHistory(BASE_URL),
  routes,
  scrollBehavior: routerConfig.scrollBehavior
})

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Si l'utilisateur est sur la page racine
  if (to.path === '/') {
    return next('/dashboard')
  }
  
  // Vérifier si la route nécessite une authentification
  if (to.meta.requiresAuth === true) { // Vérification explicite de true
    if (!authStore.isAuthenticated) {
      // Sauvegarder l'URL de retour
      authStore.returnUrl = to.fullPath
      return next('/login')
    }
    
    try {
      // Vérifier la validité de la session
      await authStore.checkSession()
      return next()
    } catch {
      return next('/login')
    }
  }
  
  next()
})

export default router 